import {
  jobsGrid,
  scheduledJobs,
  scheduledEvent,
  eventData,
  mobileJobsGrid,
  targetEventTypes,
  postJobTypes,
  getJobForSageTypes,
  targetJobTypes,
  postJobToSageTypes,
  fileDocumentTypes,
  updateJobTypes,
  getJobsForInvoiceDropdownTypes,
  postSchedulerJobTypes,
  dropDown
} from './interfaces'
import { DateFormatter, DateTimeFormatter } from '../data/helper'
import { getQuotesByJobId, JobQuote } from './quoteData'
import axios from 'axios'

const apiURL = process.env.REACT_APP_API_URL

const dateFormatter = new DateFormatter()
const dateTimeFormatter = new DateTimeFormatter()

//#region getJobs
export const getJobs = async (
  showCompleted: boolean,
  showDeleted: boolean,
  showEngineerComments: boolean,
  ShowRemedialWorks: boolean
): Promise<jobsGrid[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<jobsGrid[]>(
    `https://${apiURL}/Jobs?ShowCompleted=${showCompleted}&ShowDeleted=${showDeleted}&ShowEngineerComments=${showEngineerComments}&ShowRemedialWorks=${ShowRemedialWorks}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const jobData: jobsGrid[] = response.data.map((item) => ({
    jobsheetCount: item.jobsheetCount,
    jobsheetReferralCount: item.jobsheetReferralCount,
    noteCount: item.noteCount,
    bookedStartDate: item.bookedStartDate,
    bookedEndDate: item.bookedEndDate,
    jobId: item.jobId,
    ref: item.ref,
    refFormatted: item.refFormatted,
    description: item.description,
    department: item.department,
    startDate: dateFormatter.format(item.startDate),
    endDate: dateFormatter.format(item.endDate),
    client: item.client,
    site: item.site,
    worktype: item.worktype,
    priority: item.priority,
    priorityId: item.priorityId,
    createdDate: dateFormatter.format(item.createdDate),
    deletedDate: item.deletedDate ? dateFormatter.format(item.deletedDate) : '',
    completedDate: item.completedDate
      ? dateFormatter.format(item.completedDate)
      : '',
    createdBy: item.createdBy,
    employeeProjectmanager: item.employeeProjectmanager,
    isBooked: item.isBooked,
    scheduledEndDate: item.scheduledEndDate,
    scheduledStartDate: item.scheduledStartDate,
    note1Details: item.note1Details,
    note2Details: item.note2Details,
    note3Details: item.note3Details,
    note1Completed: item.note1Completed,
    note2Completed: item.note2Completed,
    note3Completed: item.note3Completed,
    employeesAssignedTo: item.employeesAssignedTo,
    highPriority: item.highPriority,
    inDefect: item.inDefect,
  }))
  return jobData
}
//#endregion

//#region getJobsWithPagination
export const getJobsWithPagination = async (params: {
  skip: number
  take: number
  showCompleted: boolean
  showDeleted: boolean
  showEngineerComments: boolean
  showRemedialWorks: boolean
  allocationType: string
  sort: any[]
  filter: any
  notifiableWorksFilter?: string[]
}): Promise<{ data: jobsGrid[], total: number }> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  // Build query string parameters
  const queryParams = new URLSearchParams({
    skip: params.skip.toString(),
    take: params.take.toString(),
    ShowCompleted: params.showCompleted.toString(),
    ShowDeleted: params.showDeleted.toString(),
    ShowEngineerComments: params.showEngineerComments.toString(),
    ShowRemedialWorks: params.showRemedialWorks.toString(),
    AllocationType: params.allocationType,
  })

  // Add sorting parameters if present
  if (params.sort && params.sort.length > 0) {
    // Use only the first sort item (primary sort)
    const primarySort = params.sort[0]
    queryParams.append('sortField', primarySort.field)
    queryParams.append('sortDir', primarySort.dir)
  }

  // Add filter parameters if present
  if (params.filter) {
    queryParams.append('filter', JSON.stringify(params.filter))
  }

  // Add notifiable works filter if present
  if (params.notifiableWorksFilter && params.notifiableWorksFilter.length > 0) {
    queryParams.append('notifiableWorksFilter', params.notifiableWorksFilter.join(','))
  }

  const url = `https://${apiURL}/Jobs/paginated?${queryParams.toString()}`
  console.log('API URL:', url)
  console.log('Params:', { skip: params.skip, take: params.take })

  const response = await axios.get<{ data: jobsGrid[], total: number }>(
    url,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  // Transform the data to match our interface
  const jobData: jobsGrid[] = response.data.data.map((item) => ({
    ...item,
    startDate: dateFormatter.format(item.startDate),
    endDate: dateFormatter.format(item.endDate),
    createdDate: dateFormatter.format(item.createdDate),
    deletedDate: item.deletedDate ? dateFormatter.format(item.deletedDate) : '',
    completedDate: item.completedDate
      ? dateFormatter.format(item.completedDate)
      : '',
  }))

  return {
    data: jobData,
    total: response.data.total
  }
}
//#endregion

export const getJobsForJobsFilterDropdown = async (searchTerm: string): Promise<dropDown[]> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get(`https://${apiURL}/Jobs/ForFilterDropdown?filter=${encodeURIComponent(searchTerm)}`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });

    const data = response.data;

    const clientDropdownData: dropDown[] = (data as dropDown[]).map((item: any) => ({
        text: item.text,
        value: item.value,
    }));

    return clientDropdownData;
};

//#region submitEvent
/**
 * Books a job into the diary, one entry per day. includeWeekends decides whether
 * an entry over several days books the Saturdays and Sundays in it or skips them.
 */
export const submitEvent = async (
  id: number,
  startDate: string,
  endDate: string,
  employeeId: number[],
  includeWeekends = true
) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }
  try {
    const response = await axios({
      url: `https://${apiURL}/Appointment`,
      method: 'post',
      data: {
        jobId: id,
        startDate: startDate,
        endDate: endDate,
        employeeId: employeeId,
        includeWeekends: includeWeekends,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data = response.data

    return data
  } catch {
    throw new Error('Error creating job');
  } 
}
//#endregion

//#region removeSchedulerJob
export const removeSchedulerJob = async (jobId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }
  try {
    const response = await axios({
      url: `https://${apiURL}/Appointment/${jobId}`,
      method: 'delete',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data: postJobTypes = response.data

    return data
  } catch {
    throw new Error('Error creating job');
  } 
}
//#endregion

//#region Delete a whole diary booking
export interface BookingRunEntry {
  appointmentId: number
  startDate: string
  endDate: string
}

export interface BookingRun {
  /** Entries from today onwards that would be deleted, oldest first. */
  toDelete: BookingRunEntry[]
  /** Entries in the booking before today, which are kept. */
  pastEntriesKept: number
}

/**
 * The entries that deleting the whole booking an appointment belongs to would
 * remove: the same job and crew on the days either side, stepping over
 * weekends and bank holidays, from today onwards.
 */
export const getBookingRun = async (appointmentId: number): Promise<BookingRun> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<BookingRun>(
    `https://${apiURL}/Appointment/${appointmentId}/Booking`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  return response.data
}

export const deleteBookingRun = async (appointmentId: number): Promise<number> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.delete<{ deleted: number }>(
    `https://${apiURL}/Appointment/${appointmentId}/Booking`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  return response.data.deleted
}
//#endregion

//#region getJobsForSchedulerList
export const getJobsForSchedulerList = async (
  showCompleted: boolean,
  showDeleted: boolean,
  filterIds?: number[]
): Promise<scheduledJobs[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  let url = `https://${apiURL}/Jobs/JobScheduler?ShowCompleted=${showCompleted}&ShowDeleted=${showDeleted}`
  
  if (filterIds && filterIds.length > 0) {
    filterIds.forEach((id) => {
      url += `&ScheduledEmployeeIds=${id}`;
    });
  } 

  const response = await axios.get<scheduledJobs[]>(
    url,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const jobData: scheduledJobs[] = response.data.map((item) => ({
    jobId: item.jobId,
    ref: item.ref,
    refFormatted: item.refFormatted,
    client: item.client,
    site: item.site,
    worktype: item.worktype,
    scheduledStartDate: dateTimeFormatter.formatDateTime(item.scheduledStartDate),
    scheduledEndDate: dateTimeFormatter.formatDateTime(item.scheduledEndDate),
    scheduledEmployeeId: item.scheduledEmployeeId,
    createdDate: dateFormatter.format(item.createdDate),
    deletedDate: item.deletedDate ? dateFormatter.format(item.deletedDate) : '',
    completedDate: item.completedDate
      ? dateFormatter.format(item.completedDate)
      : '',
    description: item.description,
    employeeProjectmanager: item.employeeProjectmanager,
    employeeProjectmanagerId: item.employeeProjectmanagerId,
    worktypegroupId: item.worktypegroupId,
  }))

  return jobData
}
//#endregion

//#region getEventsForScheduler
export const getEventsForScheduler = async (
  showCompleted: boolean,
  showDeleted: boolean,
  filterIds?: number[]
): Promise<scheduledEvent[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  let url = `https://${apiURL}/Appointment?ShowCompleted=${showCompleted}&ShowDeleted=${showDeleted}`
  
  if (filterIds && filterIds.length > 0) {
    filterIds.forEach((id) => {
      url += `&ScheduledEmployeeIds=${id}`;
    });
  } 

  const response = await axios.get<eventData[]>(
    url,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )


  const jobData: scheduledEvent[] = response.data.map((item) => ({
    id: item.id,
    jobId: item.jobId,
    jobRef: item.jobRef,
    title: item.siteAddr + " - " + item.jobRef + " - " + item.jobDescription,
    siteAddr: item.siteAddr,
    projectManager: item.projectManagerName,
    client: item.clientName,
    description: item.jobDescription,
    start: new Date(item.start),
    end: new Date(item.end),
    employeeIds: item.employeeIds ?? [],
    createdBy: item.createdBy ?? null,
    createdDate: item.createdDate ? new Date(item.createdDate) : null,
    modifiedBy: item.modifiedBy ?? null,
    modifiedDate: item.modifiedDate ? new Date(item.modifiedDate) : null,
  }))

  return jobData
}
//#endregion

//#region getTargetEvent
export const getTargetEvent = async (id: number): Promise<targetEventTypes> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Appointment/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: targetEventTypes = response.data

  const targetEventData: targetEventTypes = {
    appointmentId: data.appointmentId,
    jobId: data.jobId,
    startDate: new Date(data.startDate),
    endDate: new Date(data.endDate),
    assignedEmployees: data.assignedEmployees ? data.assignedEmployees.map((employee) => ({
      employeeId: employee.employeeId,
      employeeName: employee.employeeName,
    })) : []
  }

  return targetEventData
}
//#endregion

//#region updateEvent
export const updateEvent = async (appointmentId: number, startDate: Date, endDate: Date, employeeId: number[]) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const toLocalISOString = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };
  const startDateStr = toLocalISOString(startDate);
  const endDateStr = toLocalISOString(endDate);

  try {
    const response = await axios({
      url: `https://${apiURL}/Appointment`,
      method: 'put',
      data: {
        appointmentId: appointmentId,
        startDate: startDateStr,
        endDate: endDateStr,
        employeeId: employeeId,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data = response.data

    return data

  } catch {
    throw new Error('Error updating job');
  }
}
//#endregion

//#region updateJobDescription
export const updateJobDescription = async (jobId: number, description: string) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  try {
    const response = await axios({
      url: `https://${apiURL}/Jobs/Description/${jobId}`,
      method: 'put',
      data: {
        description: description,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    return response.data

  } catch {
    throw new Error('Error updating job description');
  }
}
//#endregion

//#region submitJob
export const submitJob = async (job: postJobTypes) => {
  const token = localStorage.getItem('authToken')
  if (!token) {
    throw new Error('Authorization token not found')
  }
  try {
    const response = await axios({
      url: `https://${apiURL}/Jobs`,
      method: 'post',
      data: {
        clientId: job.clientId,
        siteId: job.siteId,
        priorityId: job.priorityId,
        enquirysourceId: job.enquirysourceId ? job.enquirysourceId : 0,
        description: job.description,
        departmentId: 1,
        purchaseOrder: job.purchaseOrder,
        worktypeId: job.worktypeId,
        cost: job.cost,
        proformaCost: job.proformaCost,
        firstHourInvoiced: job.firstHourInvoiced,
        startDate: job.startDate,
        endDate: job.endDate,
        employeeProjectManagerId: job.employeeProjectManagerId,
        quoteId: job.quoteId,
        jobEmployeeIds: job.jobEmployeeIds,
        enquiryId: job.enquiryId,
        contactId: job.contactId,
        notifiableWorks: job.notifiableWorks,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data: postJobTypes = response.data
    
    // const jobId = data.jobId

    // if (jobId) {
    //   addJobToSage(jobId);
    // } else {
    //   throw new Error('Job ID not found in response');
    // }

    return data
  } catch {
    throw new Error('Error creating job');
  } 
}
//#endregion

//#region submitJobSageTest

export const submitJobSageTest = async (job: postJobTypes) => {
  const token = localStorage.getItem('authToken')
  // this adds the job, ignore the other one on the same route...
  if (!token) {
    throw new Error('Authorization token not found')
  }
  try {
    const response = await axios({
      url: `https://${apiURL}/Jobs`,
      method: 'post',
      data: {
        clientId: job.clientId,
        siteId: job.siteId,
        contactId: job.contactId,
        priorityId: job.priorityId,
        enquirysourceId: job.enquirysourceId ? job.enquirysourceId : 0,
        description: job.description,
        departmentId: 1,
        purchaseOrder: job.purchaseOrder,
        worktypeId: job.worktypeId,
        cost: job.cost,
        proformaCost: job.proformaCost,
        firstHourInvoiced: job.firstHourInvoiced,
        startDate: job.startDate,
        endDate: job.endDate,
        employeeProjectManagerId: job.employeeProjectManagerId,
        quoteId: job.quoteId,
        jobEmployeeIds: job.jobEmployeeIds,
        enquiryId: job.enquiryId,
        notifiableWorks: job.notifiableWorks,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data: postJobTypes = response.data

    return data
  } catch {
    throw new Error('Error creating job');
  }
}

//#endregion

//#region addJobToSage
/*
export const addJobToSage = async (jobId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    
    url: `https://${apiURL}/Jobs/AddToSage/${jobId}`,
    method: 'post',
    
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  return data;
}
  */
//#endregion

//#region addJobToSageTest

  
//#endregion

//#region updateJobSageTest

export const updateJobSageTest = async (job: updateJobTypes, jobId: number) => {
  const token = localStorage.getItem('authToken')
  console.log("JOB", job)

  if (!token) {
    throw new Error('Authorization token not found')
  }

  try {
    const response = await axios({
      url: `https://${apiURL}/Jobs/${jobId}`,
      method: 'put',
      data: {
        job: jobId,
        jobId: jobId,
        clientId: job.clientId,
        siteId: job.siteId,
        contactId: job.contactId,
        priorityId: job.priorityId,
        enquirysourceId: job.enquirysourceId,
        description: job.description,
        departmentId: job.departmentId,
        purchaseOrder: job.purchaseOrder,
        externallyBooked: job.externallyBooked,
        externallyBookedName: job.externallyBookedName,
        worktypeId: job.worktypeId,
        cost: job.cost,
        proformaCost: job.proformaCost,
        firstHourInvoiced: job.firstHourInvoiced,
        startDate: job.startDate,
        endDate: job.endDate,
        employeeProjectManagerId: job.employeeProjectmanager,
        quoteId: job.quoteId,
        jobEmployeeIds: (job.jobEmployeeIds || []).map((id: any) => parseInt(id, 10)).filter((id: number) => id && id !== 0),
        completedDate: job.completedDate,
        deletedDate: job.deletedDate,
        scheduledStartDate: job.scheduledStartDate,
        scheduledEndDate: job.scheduledEndDate,
        projectFolderUrl: job.projectFolderUrl,
        notifiableWorks: job.notifiableWorks,
        inDefect: job.inDefect,
        enableSubcontractAgreements: job.enableSubcontractAgreements,
        workingsAdjustmentPercent: job.workingsAdjustmentPercent ?? null,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data: updateJobTypes = response.data

    return data

  } catch(error) {
    console.log(error)
    throw new Error('Error updating job');
  } 
}
  
//#endregion

//#region updateJob
export const updateJob = async (job: updateJobTypes, jobId: number) => {
  const token = localStorage.getItem('authToken')
  if (!token) {
    throw new Error('Authorization token not found')
  }
  try {
    const response = await axios({
      url: `https://${apiURL}/Jobs/${jobId}`,
      method: 'put',
      data: {
        jobId: jobId,
        clientId: job.clientId,
        siteId: job.siteId,
        contactId: job.contactId,
        priorityId: job.priorityId,
        enquirysourceId: job.enquirysourceId,
        description: job.description,
        departmentId: job.departmentId,
        purchaseOrder: job.purchaseOrder,
        worktypeId: job.worktypeId,
        cost: job.cost,
        proformaCost: job.proformaCost,
        firstHourInvoiced: job.firstHourInvoiced,
        startDate: job.startDate,
        endDate: job.endDate,
        employeeProjectManagerId: job.employeeProjectmanager,
        quoteId: job.quoteId,
        jobEmployeeIds: job.jobEmployeeIds,
      },
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    })

    const data: updateJobTypes = response.data

    //updateJobToSage(jobId)

    return data

  } catch {
    throw new Error('Error updating job');
  } 
}
//#endregion

//#region updateJobToSage
/*
export const updateJobToSage = async (jobId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    
    url: `https://${apiURL}/Jobs/UpdateToSage/${jobId}`,
    method: 'post',
    
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data: postJobToSageTypes = response.data

  return data;
}
  */
//#endregion

//#region getTargetJob
export const getTargetJob = async (jobId: number): Promise<targetJobTypes> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Jobs/${jobId}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })
  
  const data: targetJobTypes = response.data

  // Fetch quotes for this job
  let quotes: JobQuote[] = []
  try {
    quotes = await getQuotesByJobId(jobId)
  } catch (error) {
    console.warn('Failed to fetch quotes for job:', error)
    quotes = []
  }

  const targetJobData: targetJobTypes = {
    // Carried through explicitly: this builder reshapes the response field
    // by field, so anything not named here is dropped before it reaches the form.
    workingsAdjustmentPercent: data.workingsAdjustmentPercent ?? null,
    invoiceItems: data.invoiceItems,
    contact: {
      contactId: data.contact?.contactId,
      title: data.contact?.title,
      firstname: data.contact?.firstname,
      lastname: data.contact?.lastname,
      phone: data.contact?.phone,
      email: data.contact?.email,
    },
    jobsheets: data.jobsheets,
    enquiryId: data.enquiryId,
    bookedEndDate: data.bookedEndDate,
    bookedStartDate: data.bookedStartDate,
    quoteRef: data.quoteRef,
    enquiryRef: data.enquiryRef,
    jobId: data.jobId,
    ref: data.ref,
    refFormatted: data.refFormatted,
    description: data.description,
    purchaseOrder: data.purchaseOrder,
    externallyBooked: data.externallyBooked || false,
    externallyBookedName: data.externallyBookedName || '',
    startDate: data.startDate,
    endDate: data.endDate,
    scheduledStartDate: data.scheduledStartDate,
    scheduledEndDate: data.scheduledEndDate,
    completedDate: data.completedDate,
    createdDate: data.createdDate,
    deletedDate: data.deletedDate,
    cost: data.cost,
    proformaCost: data.proformaCost ? data.proformaCost : 0,
    costsToDate: data.costsToDate,
    committedCosts: data.committedCosts ?? 0,
    amountBilledToDate: data.amountBilledToDate,
    amountLeftToBill: data.amountLeftToBill,
    exportedDate: data.exportedDate,
    firstHourInvoiced: data.firstHourInvoiced,
    nominalCode: data.nominalCode,
    lastScheduledBy: data.lastScheduledBy,
    client: {
      clientId: data.client.clientId,
      clientName: data.client.clientName,
      accountRef: data.client.accountRef,
      contactName: data.client.contactName,
      phone:
        data.client && data.client.phone
          ? data.client.phone
          : 'No phone number',
      email:
        data.client && data.client.email
          ? data.client.email
          : 'No email address',
    },
    site: {
      siteId: data.site.siteId,
      siteAddr: data.site.siteAddr,
      postcode: data.site.postcode,
    },
    worktype: {
      worktypeId: data.worktype.worktypeId,
      description: data.worktype.description,
      group: data.worktype.group,
    },
    priority: {
      priorityId: data.priority.priorityId,
      description: data.priority.description,
    },
    enquirysource: {
      enquirysourceId:
        data.enquirysource && data.enquirysource.enquirysourceId
          ? data.enquirysource.enquirysourceId
          : null,
      description:
        data.enquirysource && data.enquirysource.description
          ? data.enquirysource.description
          : 'No enquiry source set',
    },
    department: {
      departmentId: data.department.departmentId,
      description: data.department.description,
    },
    employeeProjectmanager: {
      employeeId: data.employeeProjectmanager.employeeId,
      employeeName: data.employeeProjectmanager.employeeName,
      position: data.employeeProjectmanager.position,
    },
    quote: {
      quoteId: data.quote && data.quote.quoteId ? data.quote.quoteId : null,
      ref: data.quote && data.quote.ref ? data.quote.ref : null,
      refFormatted:
        data.quote && data.quote.refFormatted ? data.quote.refFormatted : null,
    },
    quotes: quotes,
    employees: data.employees
      ? data.employees.map((employee) => ({
          employeeId: employee.employeeId,
          employeeName: employee.employeeName,
          position: employee.position,
        }))
      : [],
      projectFolderUrl: data.projectFolderUrl,
      variations: data.variations || [],
      notifiableWorks: data.notifiableWorks || '',
      inDefect: data.inDefect || false,
      enableSubcontractAgreements: data.enableSubcontractAgreements || false,
  }

  return targetJobData
}
//#endregion

//#region exportJobs
export const exportJobs = async () => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Jobs/ExportJobs`, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'jobs.csv')
  document.body.appendChild(link)
  link.click()
}
//#endregion

//#region exportJobSheet
export const exportJobsheet = async (jobId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/PDF/Jobsheet/${jobId}`, {
    responseType: 'blob',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', 'jobsheet.pdf')
  document.body.appendChild(link)
  link.click()
}
//#endregion

//#region completeJob
export const completeJob = async (jobId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Jobs/MarkAsComplete/${jobId}`,
    method: 'put',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  return data
}
//#endregion

//#region deleteJob
export const deleteJob = async (jobId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios({
    url: `https://${apiURL}/Jobs/${jobId}`,
    method: 'delete',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  const data = response.data

  return data
}
//#endregion

//#region getPortalJobs
export const getPortalJobs = async (): Promise<mobileJobsGrid[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<mobileJobsGrid[]>(
    `https://${apiURL}/Jobs/ForCurrentUser`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const jobData: mobileJobsGrid[] = response.data.map((item) => ({
    jobId: item.jobId,
    ref: item.ref,
    refFormatted: item.refFormatted,
    startDate: dateFormatter.format(item.startDate),
    endDate: dateFormatter.format(item.endDate),
    client: item.client,
    site: item.site,
  }))

  return jobData
}
//#endregion

//#region getJobsForInvoiceDropdown
export const getJobsForInvoiceDropdown = async (id: number): Promise<getJobsForInvoiceDropdownTypes[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<getJobsForInvoiceDropdownTypes[]>(
    `https://${apiURL}/Jobs/ForInvoicingDropdown/${id}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const jobDropdownData: getJobsForInvoiceDropdownTypes[] = response.data.map((item) => ({
    disabled: item.disabled,
    group: item.group,
    selected: item.selected,
    text: item.text,
    value: item.value
  }))
  return jobDropdownData
}
//#endregion

//#region getJobsDropdownClientSpecific
export const getJobsForDropdownClientSpecific = async (id: number): Promise<getJobsForInvoiceDropdownTypes[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<getJobsForInvoiceDropdownTypes[]>(
    `https://${apiURL}/Jobs/ForDropdownClientSpecific/${id}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const jobDropdownData: getJobsForInvoiceDropdownTypes[] = response.data.map((item) => ({
    disabled: item.disabled,
    group: item.group,
    selected: item.selected,
    text: item.text,
    value: item.value
  }))
  return jobDropdownData
}
//#endregion

//#region getJobsForDropdown
export const getJobsForDropdown = async (): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<dropDown[]>(
    `https://${apiURL}/Jobs/ForDropdown`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  console.log('Raw Jobs data:', response.data)
  return response.data.map((item: any) => {
    const numericValue = Number(item.value)
    return {
      text: item.text || 'Unknown',
      value: isNaN(numericValue) ? 0 : numericValue
    }
  })
}
//#endregion

//#region moveJobCost
export const moveJobCost = async (jobCostId: number, targetJobId: number): Promise<void> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.put(
    `https://${apiURL}/JobCosts/Move/${jobCostId}/${targetJobId}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  
  return response.data
}
//#endregion

//#region getJobDocuments
export const getDocuments = async (jobId: number, quoteId: number): Promise<fileDocumentTypes[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(
    `https://${apiURL}/Jobs/${jobId}/${quoteId}/Documents`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  const data: fileDocumentTypes[] = response.data

  return data
}
//#endregion

export const deleteAllFutureJobs = async (jobId: number): Promise<void> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.delete(
    `https://${apiURL}/appointment/deletefuture/${jobId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )

  console.log(response)
}


export interface JobCostingItem {
  jobCostId: number
  jobId: number
  auditNumber: string
  type: any
  accountRef: string
  date: string
  reference: string
  costCode: string
  details: string
  debit: number
  credit?: number
  amount?: number
  xeroReference: string
}


export interface AddJobCostingItemDTO {
  jobId: number
  type: string
  accountRef: string
  date: string
  reference: string
  costCode: string
  details: string
  debit: number
  credit?: number
  xeroReference?: string
}

export const addJobCostingItem = async (jobCostData: AddJobCostingItemDTO): Promise<JobCostingItem> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.post<JobCostingItem>(
    `https://${apiURL}/jobcosts`,
    jobCostData,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}

export interface UpdateJobCostingItemDTO {
  jobCostId: number
  jobId: number
  type: string
  accountRef: string
  date: string
  reference: string
  costCode: string
  details: string
  debit: number
  credit?: number
  xeroReference?: string
}

export const updateJobCostingItem = async (jobCostData: UpdateJobCostingItemDTO): Promise<JobCostingItem> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.put<JobCostingItem>(
    `https://${apiURL}/jobcosts/${jobCostData.jobCostId}`,
    jobCostData,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}

export const getJobCostingData = async (id: number): Promise<JobCostingItem[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<JobCostingItem[]>(
    `https://${apiURL}/jobcosts/job/${id}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}

export const getJobCostXeroUrl = async (jobCostId: number): Promise<string> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<{ url: string }>(
    `https://${apiURL}/jobcosts/${jobCostId}/xero-url`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data.url
}

export const getJobCostInvoiceFileUrl = async (jobCostId: number): Promise<string> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get<{ url: string }>(
    `https://${apiURL}/jobcosts/${jobCostId}/invoice-file-url`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data.url
}

export const markJobsheetAsProcessed = async (jobsheetId: number): Promise<any[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.put<any[]>(
    `https://${apiURL}/jobsheet/markasprocessed/${jobsheetId}`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}

//#region Job Variations CRUD
export const createJobVariation = async (variation: { jobId: number; description: string; cost: number }) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.post(
    `https://${apiURL}/Jobs/${variation.jobId}/variations`,
    {
      description: variation.description,
      cost: variation.cost
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}

export const updateJobVariation = async (variation: { id: number; jobId: number; description: string; cost: number }) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  console.log('Updating job variation (client-side object):', variation);
  const body = {
    // Only send fields the API needs; extra id/jobid fields previously may have interfered with binding.
    description: variation.description,
    cost: variation.cost
  } as const;
  console.log('Submitting variation update body:', body);

  const response = await axios.put(
    `https://${apiURL}/Jobs/${variation.jobId}/variations/${variation.id}`,
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}

export const deleteJobVariation = async (jobId: number, variationId: number) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.delete(
    `https://${apiURL}/Jobs/${jobId}/variations/${variationId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}

export const updateDocumentVisibility = async (jobId: number, quoteId: number, documentId: number, appCanView: boolean) => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.put(
    `https://${apiURL}/Files/${documentId}/appcanview`,
    {
      AppCanView: appCanView
    },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}
//#endregion

//#region materialMovements
export interface MaterialMovement {
  jobCostMovementId: number
  jobCostId: number
  movedDate: string
  fromJobId: number
  fromJobRef: number
  fromJobDescription: string | null
  toJobId: number
  toJobRef: number
  toJobDescription: string | null
  movedByEmployee: string | null
  accountRef: string | null
  reference: string | null
  details: string | null
  amount: number | null
}

export const getMaterialMovements = async (fromDate: Date, toDate: Date): Promise<MaterialMovement[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const from = fromDate.toISOString().split('T')[0]
  const to = toDate.toISOString().split('T')[0]

  const response = await axios.get<MaterialMovement[]>(
    `https://${apiURL}/JobCosts/movements?fromDate=${from}&toDate=${to}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
    }
  )
  return response.data
}
//#endregion

/**
 * Open jobs for a client, for the lines of an invoice that hasn't been saved
 * yet (getJobsForInvoiceDropdown needs a saved invoice to find the client).
 */
export const getJobsForInvoiceDropdownByClient = async (clientId: number): Promise<dropDown[]> => {
  const token = localStorage.getItem('authToken')

  if (!token) {
    throw new Error('Authorization token not found')
  }

  const response = await axios.get(`https://${apiURL}/Jobs/ForInvoicingDropdownByClient/${clientId}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
  })

  return response.data
}
